/**
 * AccountModal — user account management overlay.
 *
 * Accessible from the user button in the top bar.
 *
 * Features:
 *   • Shows signed-in email
 *   • Sign out button
 *   • Delete account (requires typing "DELETE" to confirm)
 */

import React, { useState } from 'react';
import { theme } from '../theme';
import { apiDelete } from '../api';
import { useAuth } from '../context/AuthContext';

export default function AccountModal({ onClose }) {
  const { user, signOut } = useAuth();

  const [phase, setPhase]       = useState('menu');   // 'menu' | 'confirm-delete'
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy]         = useState(false);
  const [error, setError]       = useState('');

  async function handleDeleteAccount() {
    if (confirmText !== 'DELETE') return;
    setBusy(true);
    setError('');
    try {
      await apiDelete('/account/delete');
      // Server deleted the Auth user — sign out locally so Firebase clears the session
      await signOut();
    } catch (err) {
      setError(err.message || 'Failed to delete account. Please try again.');
      setBusy(false);
    }
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: 16,
    }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        background: theme.surface, border: `1px solid ${theme.border}`,
        borderRadius: 12, width: '100%', maxWidth: 400,
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          padding: '16px 20px', borderBottom: `1px solid ${theme.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <h2 style={{ fontSize: 15, fontWeight: 700, color: theme.text, margin: 0 }}>
            {phase === 'confirm-delete' ? 'Delete account' : 'My account'}
          </h2>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', color: theme.textMuted,
            cursor: 'pointer', fontSize: 20, lineHeight: 1, padding: 0,
          }}>×</button>
        </div>

        {/* Body */}
        <div style={{ padding: '20px 20px 24px' }}>

          {phase === 'menu' && (
            <>
              {/* Account info */}
              <div style={{
                background: theme.bg, border: `1px solid ${theme.border}`,
                borderRadius: 8, padding: '12px 14px', marginBottom: 20,
              }}>
                <p style={{ fontSize: 11, color: theme.textMuted, margin: '0 0 2px' }}>Signed in as</p>
                <p style={{ fontSize: 14, color: theme.text, margin: 0, fontWeight: 500, wordBreak: 'break-all' }}>
                  {user?.email}
                </p>
              </div>

              {/* Sign out */}
              <button onClick={signOut} style={{
                width: '100%', padding: '9px 14px', borderRadius: 7,
                border: `1px solid ${theme.border}`,
                background: 'none', color: theme.text, fontSize: 13,
                cursor: 'pointer', textAlign: 'left', marginBottom: 10,
              }}>
                Sign out
              </button>

              {/* Delete account */}
              <button onClick={() => { setPhase('confirm-delete'); setConfirmText(''); setError(''); }} style={{
                width: '100%', padding: '9px 14px', borderRadius: 7,
                border: `1px solid ${theme.danger}55`,
                background: 'none', color: theme.danger, fontSize: 13,
                cursor: 'pointer', textAlign: 'left',
              }}>
                Delete account…
              </button>
            </>
          )}

          {phase === 'confirm-delete' && (
            <>
              <div style={{
                background: `${theme.danger}12`, border: `1px solid ${theme.danger}44`,
                borderRadius: 8, padding: '12px 14px', marginBottom: 18,
              }}>
                <p style={{ fontSize: 13, color: theme.danger, margin: '0 0 6px', fontWeight: 600 }}>
                  This action is permanent and cannot be undone.
                </p>
                <p style={{ fontSize: 13, color: theme.textMuted, margin: 0, lineHeight: 1.5 }}>
                  Your account, saved queries, and connections will be permanently deleted.
                  Any active subscription will be cancelled immediately.
                </p>
              </div>

              <label style={{ display: 'block', marginBottom: 16 }}>
                <span style={{ fontSize: 12, color: theme.textMuted, display: 'block', marginBottom: 6 }}>
                  Type <strong style={{ color: theme.text }}>DELETE</strong> to confirm
                </span>
                <input
                  type="text"
                  autoFocus
                  value={confirmText}
                  onChange={e => setConfirmText(e.target.value)}
                  placeholder="DELETE"
                  style={{
                    width: '100%', boxSizing: 'border-box',
                    background: theme.bg, border: `1px solid ${theme.border}`,
                    borderRadius: 6, padding: '8px 10px',
                    color: theme.text, fontSize: 14, outline: 'none',
                  }}
                />
              </label>

              {error && (
                <p style={{ fontSize: 12, color: theme.danger, marginBottom: 12 }}>{error}</p>
              )}

              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => { setPhase('menu'); setError(''); }} disabled={busy} style={{
                  flex: 1, padding: '8px 14px', borderRadius: 7,
                  border: `1px solid ${theme.border}`,
                  background: 'none', color: theme.textMuted,
                  fontSize: 13, cursor: busy ? 'not-allowed' : 'pointer',
                }}>
                  Cancel
                </button>
                <button
                  onClick={handleDeleteAccount}
                  disabled={confirmText !== 'DELETE' || busy}
                  style={{
                    flex: 1, padding: '8px 14px', borderRadius: 7,
                    border: 'none',
                    background: confirmText === 'DELETE' && !busy ? theme.danger : `${theme.danger}44`,
                    color: '#fff', fontSize: 13,
                    cursor: confirmText === 'DELETE' && !busy ? 'pointer' : 'not-allowed',
                    transition: 'background 0.15s',
                  }}
                >
                  {busy ? 'Deleting…' : 'Delete my account'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
